import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
import { AlertCircle, CheckCircle, Loader } from "lucide-react";

export default function DashboardAccessControl() {
  const [employeeId, setEmployeeId] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);

  const [permissions, setPermissions] = useState({
    dashboard_admin: false,
    dashboard_coordinator: true,
    dashboard_kpkb: false,
    dashboard_cosmesia: false,
    dashboard_expense: false,

    add_area: true,
    add_route: true,
    add_doctor: true,
    edit_doctor: true,
    add_chemist: true,
    edit_chemist: true,
    add_stockist: true,
    edit_stockist: true,
    assign_doctor: true,
    assign_chemist: true,
    assign_stockist: true,
    add_institute: true,
    edit_institute: true,
    deviate_tp: true,
  });

  const [otherPermissions, setOtherPermissions] = useState({
    add_area: true,
    add_route: true,
    add_doctor: true,
    edit_doctor: true,
    add_chemist: true,
    edit_chemist: true,
    add_stockist: true,
    edit_stockist: true,
    assign_doctor: true,
    assign_chemist: true,
    assign_stockist: true,
    add_institute: true,
    edit_institute: true,
    deviate_tp: true,
  });

  const dashboardOptions = [
    {
      id: "dashboard_admin",
      label: "Admin Dashboard",
      description: "Full admin access and controls",
      icon: "⚙️",
    },
    {
      id: "dashboard_coordinator",
      label: "Coordinator Dashboard",
      description: "Manage coordination activities",
      icon: "👥",
    },
    {
      id: "dashboard_kpkb",
      label: "KPKB Dashboard",
      description: "Key Performance Indicators & KPI Boards",
      icon: "📊",
    },
    {
      id: "dashboard_cosmesia",
      label: "Cosmesia Dashboard",
      description: "Cosmesia module access",
      icon: "💎",
    },
    {
      id: "dashboard_expense",
      label: "Expense Dashboard",
      description: "Expense tracking and management",
      icon: "💰",
    },
  ];

  const handleToggle = (key) => {
    setPermissions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleUpdatePermissions = async () => {
    if (!employeeId.trim()) {
      setMessage("Please enter an Employee ID");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const payload = {
        employee_ids: [Number(employeeId)],
        ...permissions,
        report_period: 0,
        manager_expense_type: true,
      };

      const response = await axios.post(
        `${BASE_URL}/api/employees-permissions-bulk`,
        payload,
        {
          headers: getAuthHeaders(),
        }
      );

      setMessage("Dashboard permissions updated successfully!");
      setMessageType("success");
      console.log("Response:", response.data);
    } catch (error) {
      setMessage(
        error.response?.data?.message ||
          "Failed to update permissions. Please try again."
      );
      setMessageType("error");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
      <div className="min-h-screen w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto w-full">
        <div className="text-center">
           <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent sm:text-6xl">
              Dashboard Access Control
            </h1>
          <p className="mt-2 text-lg text-slate-400">
            Manage user permissions for different dashboard modules
          </p>
        </div>

        {/* Employee ID Input */}
        <div className="mb-8 rounded-xl border border-slate-700 bg-slate-800 p-6 shadow-lg">
          <label className="block text-sm font-semibold text-slate-200">
            Employee ID
          </label>
          <input
            type="number"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            placeholder="Enter employee ID"
            className="mt-3 w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white placeholder-slate-500 outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          />
        </div>

        {/* Message Alert */}
        {message && (
          <div
            className={`mb-6 flex items-center gap-3 rounded-lg border px-4 py-3 ${
              messageType === "success"
                ? "border-green-500/30 bg-green-500/10 text-green-300"
                : "border-red-500/30 bg-red-500/10 text-red-300"
            }`}
          >
            {messageType === "success" ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span className="text-sm font-medium">{message}</span>
          </div>
        )}

        {/* Dashboard Permissions Grid */}
        <div className="mb-10">
          <h2 className="mb-6 text-2xl font-bold text-white">
            Dashboard Modules
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {dashboardOptions.map((option) => (
              <div
                key={option.id}
                className="group relative rounded-xl border border-slate-700 bg-gradient-to-br from-slate-800 to-slate-900 p-6 shadow-lg transition-all hover:border-slate-600 hover:shadow-xl"
              >
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{option.icon}</span>
                    <div>
                      <h3 className="font-semibold text-white">
                        {option.label}
                      </h3>
                      <p className="text-xs text-slate-400">
                        {option.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <ToggleSwitch
                    className="w-full"
                    checked={permissions[option.id]}
                    onChange={() => handleToggle(option.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active Permissions Summary */}
        <div className="mb-10 rounded-xl border border-slate-700 bg-slate-800 p-6 shadow-lg">
          <h3 className="mb-4 text-lg font-semibold text-white">
            Active Permissions
          </h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(permissions).map(([key, value]) => {
              if (value) {
                const label = dashboardOptions.find(
                  (opt) => opt.id === key
                )?.label;
                return (
                  <span
                    key={key}
                    className="inline-flex items-center gap-2 rounded-full bg-blue-500/20 px-4 py-2 text-sm font-medium text-blue-300 border border-blue-500/30"
                  >
                    <span className="h-2 w-2 rounded-full bg-blue-400"></span>
                    {label}
                  </span>
                );
              }
              return null;
            })}
            {!Object.values(permissions).some((v) => v) && (
              <span className="text-slate-400">
                No dashboard modules selected
              </span>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleUpdatePermissions}
          disabled={loading}
          className="w-full rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-blue-800 disabled:from-slate-600 disabled:to-slate-700 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader size={20} className="animate-spin" />
              Updating...
            </>
          ) : (
            "Update Dashboard Permissions"
          )}
        </button>
      </div>
    </div>
  );

  function ToggleSwitch({ checked, onChange }) {
    return (
      <button
        onClick={onChange}
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
          checked ? "bg-blue-600" : "bg-slate-600"
        }`}
        role="switch"
        aria-checked={checked}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    );
  }
}
